import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { format, formatDistanceToNow, subDays } from 'date-fns';
import api from '../../../services/api';
import { patientNotesQueryKeyPrefix } from '../../../hooks/queries/usePatientNotes';
import { UnifiedNote, NoteItem, NotesResponse } from './types';
import { NoteDetailModal } from './NoteDetailModal';
import { NotesFilterBar, FilterState } from './NotesFilterBar';
import { notesGridSx } from './notesGridSx';

interface ComprehensiveNotesPanelProps {
  patientId: string;
}

export const ComprehensiveNotesPanel: React.FC<ComprehensiveNotesPanelProps> = ({ patientId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const EMPTY_NOTES: NotesResponse = useMemo(
    () => ({
      deliveryOrderNotes: [],
      clinicalNotes: [],
      deliveryInstructions: [],
      subscriptionNotes: [],
      troubleshootingNotes: [],
    }),
    [],
  );

  const [selectedNote, setSelectedNote] = useState<UnifiedNote | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    selectedTypes: [],
    dateRange: 'all',
    customStartDate: null,
    customEndDate: null,
  });

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  // The read is a TanStack query on the patient's notes key (PRX-80 Task 9 fix
  // round 2), so a permanent-note save on the Patient info tab — which
  // invalidates `patientNotesQueryKeyPrefix(compositeId)` — reaches it.
  //
  // Fix round 3: that tab and this one are never mounted together. The save
  // lands while this query is INACTIVE, so the invalidation only marks it
  // (`refetchType: 'active'`), and the app-wide `refetchOnMount: false`
  // (providers/QueryProvider.tsx) never looks at staleness on the way back —
  // the tab kept showing the old note. `refetchOnMount: true` opts this one
  // query back in: an invalidated query is stale, so it reads again exactly
  // after a save and stays quiet inside `staleTime` otherwise.
  const notesQuery = useQuery({
    queryKey: [...patientNotesQueryKeyPrefix(patientId), 'all-notes'],
    enabled: !!patientId,
    refetchOnMount: true,
    queryFn: async (): Promise<NotesResponse> => {
      const response = await api.get(`/api/patients/${patientId}/all-notes`);
      // PRX-80 final review (I3): a 200 that does not report success is a
      // FAILED read, not a patient with nothing on file. Resolving it to the
      // empty notes object put "No notes found for this patient." on screen and
      // left the error Alert below unreachable — the silent fallback this repo
      // forbids. Throw, as `usePatientAllPrescriptions` does, and let the
      // panel's own error state say the lookup failed.
      if (!response.data?.success) throw new Error('Failed to fetch notes');
      return response.data.notes;
    },
  });
  const rawNotes: NotesResponse = notesQuery.data ?? EMPTY_NOTES;
  // Only the FIRST load — nothing to show yet — is a spinner. A refetch keeps
  // the grid on screen, the same rule `NotesPanel`'s `hasLoadedOnce` keeps; with
  // `|| isFetching` every refetch, including the one a save now triggers,
  // blanked the grid.
  const loading = notesQuery.isLoading;
  const error: string | null = notesQuery.isError
    ? (notesQuery.error as any)?.response?.data?.message || 'Failed to fetch notes'
    : null;
  const fetchNotes = () => notesQuery.refetch();

  // Transform raw notes into unified format
  const unifiedNotes = useMemo<UnifiedNote[]>(() => {
    const notes: UnifiedNote[] = [];

    // Transform delivery order notes
    rawNotes.deliveryOrderNotes.forEach((item: NoteItem) => {
      notes.push({
        id: `delivery-order-${item.id}`,
        type: 'delivery',
        category: 'Delivery Order',
        content: item.note || '',
        timestamp: new Date(item.createdAt),
        author: item.createdBy || 'System',
        source: item.orderNumber ? `Order #${item.orderNumber}` : 'Order',
        sourceId: item.orderId || item.id,
        sourceType: 'order',
        metadata: {
          orderNumber: item.orderNumber,
          rxNumber: item.rxNumber,
          medicationName: item.medicationName,
          workflowStage: item.workflowStage,
          updatedAt: item.updatedAt,
        },
      });
    });

    // Transform clinical notes
    rawNotes.clinicalNotes.forEach((item: NoteItem) => {
      const isFromCVMetadata = (item as any)._source === 'clinical_verification_metadata';
      notes.push({
        id: `clinical-${item.id}`,
        type: 'clinical',
        category: isFromCVMetadata ? 'Clinical Verification' : 'Clinical',
        content: item.note || '',
        timestamp: new Date(item.createdAt),
        author: item.clinicalVerifiedBy || 'System',
        source: item.orderNumber ? `Order #${item.orderNumber}` : 'Order',
        sourceId: item.orderId || item.id,
        sourceType: 'order',
        metadata: {
          orderNumber: item.orderNumber,
          rxNumber: item.rxNumber,
          medicationName: item.medicationName,
          workflowStage: item.workflowStage,
          clinicalVerifiedAt: item.clinicalVerifiedAt,
          clinicalVerifiedBy: item.clinicalVerifiedBy,
          updatedAt: item.updatedAt,
        },
      });
    });

    // Transform delivery instructions
    rawNotes.deliveryInstructions.forEach((item: NoteItem) => {
      notes.push({
        id: `delivery-instructions-${item.id}`,
        type: 'delivery',
        category: 'Delivery Instructions',
        content: item.instructions || '',
        timestamp: new Date(item.createdAt),
        author: item.createdBy || 'System',
        source: item.orderNumber ? `Order #${item.orderNumber}` : 'Order',
        sourceId: item.orderId || item.id,
        sourceType: 'order',
        metadata: {
          orderNumber: item.orderNumber,
          rxNumber: item.rxNumber,
          medicationName: item.medicationName,
          deliveryDate: item.deliveryDate,
          updatedAt: item.updatedAt,
        },
      });
    });

    // Transform subscription notes
    rawNotes.subscriptionNotes.forEach((item: NoteItem) => {
      notes.push({
        id: `subscription-${item.id}`,
        type: 'subscription',
        category: 'Subscription',
        content: item.note || '',
        timestamp: new Date(item.createdAt),
        author: item.createdBy || 'System',
        source: item.billingItemId ? `Billing Item` : 'Subscription',
        sourceId: item.billingItemId || item.id,
        sourceType: 'billing_item',
        metadata: {
          orderNumber: item.orderNumber,
          rxNumber: item.rxNumber,
          medicationName: item.medicationName,
          updatedAt: item.updatedAt,
        },
      });
    });

    // Transform troubleshooting notes
    rawNotes.troubleshootingNotes.forEach((item: NoteItem) => {
      notes.push({
        id: `troubleshooting-${item.id}`,
        type: 'troubleshooting',
        category: 'Troubleshooting',
        content: item.note || '',
        timestamp: new Date(item.createdAt),
        author: item.resolvedBy || 'System',
        source: item.orderNumber ? `Order #${item.orderNumber}` : 'Troubleshooting Log',
        sourceId: item.orderId || item.id,
        sourceType: 'troubleshooting_log',
        metadata: {
          orderNumber: item.orderNumber,
          issueType: item.issueType,
          priority: item.priority,
          status: item.status,
          resolvedAt: item.resolvedAt,
          updatedAt: item.updatedAt,
        },
      });
    });

    // Sort by timestamp descending (newest first)
    return notes.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [rawNotes]);

  // Apply filters to notes
  const filteredNotes = useMemo(() => {
    let filtered = [...unifiedNotes];

    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(note =>
        note.content.toLowerCase().includes(query) ||
        note.category.toLowerCase().includes(query) ||
        note.source.toLowerCase().includes(query) ||
        note.author?.toLowerCase().includes(query)
      );
    }

    // Type filter
    if (filters.selectedTypes.length > 0) {
      filtered = filtered.filter(note => filters.selectedTypes.includes(note.type));
    }

    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      let startDate: Date;

      switch (filters.dateRange) {
        case '7':
          startDate = subDays(now, 7);
          break;
        case '30':
          startDate = subDays(now, 30);
          break;
        case '90':
          startDate = subDays(now, 90);
          break;
        default:
          startDate = new Date(0); // Beginning of time
      }

      filtered = filtered.filter(note => note.timestamp >= startDate);
    }

    return filtered;
  }, [unifiedNotes, filters]);

  // Get type color helper
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'clinical':
        return '#4caf50';
      case 'billing':
        return '#2196f3';
      case 'delivery':
        return '#ff9800';
      case 'troubleshooting':
        return '#f44336';
      case 'subscription':
        return '#9c27b0';
      default:
        return '#757575';
    }
  };

  // Handle row click
  const handleRowClick = (params: GridRowParams) => {
    setSelectedNote(params.row as UnifiedNote);
    setModalOpen(true);
  };

  // Handle view source
  const handleViewSource = useCallback((note: UnifiedNote) => {
    // TODO: Implement navigation to source (order/billing item)
    console.log('View source:', note);
  }, []);

  // Export to CSV
  const handleExportCSV = useCallback(() => {
    const headers = ['Date/Time', 'Type', 'Content', 'Source', 'Author'];
    const rows = filteredNotes.map(note => [
      format(note.timestamp, 'MM/dd/yyyy HH:mm'),
      note.category,
      note.content.replace(/"/g, '""'), // Escape quotes
      note.source,
      note.author || 'System',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `patient-notes-${patientId}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredNotes, patientId]);

  // Define columns
  const columns = useMemo<GridColDef[]>(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'timestamp',
        headerName: 'Date/Time (mm/dd/yy hh:mm)',
        width: 150,
        sortable: true,
        valueFormatter: (value) => {
          if (!value) return '';
          return format(new Date(value as Date), 'MM/dd/yy HH:mm');
        },
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={formatDistanceToNow(new Date(params.value as Date), { addSuffix: true })}>
            <span>{format(new Date(params.value as Date), 'MM/dd/yy HH:mm')}</span>
          </Tooltip>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 140,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.row.category}
            size="small"
            sx={{
              bgcolor: getTypeColor(params.value as string),
              color: 'white',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        ),
      },
      {
        field: 'content',
        headerName: 'Preview',
        flex: 1,
        minWidth: 300,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const content = params.value as string;
          const preview = content.length > 120 ? `${content.substring(0, 120)}...` : content;
          return (
            <Tooltip title="Click to view full note">
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                {preview}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'source',
        headerName: 'Source',
        width: 150,
        sortable: true,
      },
      {
        field: 'author',
        headerName: 'Author',
        width: 130,
        sortable: true,
        valueFormatter: (value) => value || 'System',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 80,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedNote(params.row as UnifiedNote);
              setModalOpen(true);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];

    // Responsive column visibility
    if (isMobile) {
      return baseColumns.filter(col =>
        ['timestamp', 'type', 'content'].includes(col.field)
      );
    }

    if (isTablet) {
      return baseColumns.filter(col => col.field !== 'author');
    }

    return baseColumns;
  }, [isMobile, isTablet]);

  return (
    <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, pb: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            All Patient Notes
            <Chip label={unifiedNotes.length} size="small" color="primary" />
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Export to CSV">
              <span>
                <IconButton onClick={handleExportCSV} disabled={loading || filteredNotes.length === 0}>
                  <FileDownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh notes">
              <span>
                <IconButton onClick={fetchNotes} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filter Bar */}
        <NotesFilterBar
          filters={filters}
          onFiltersChange={setFilters}
          totalCount={unifiedNotes.length}
          filteredCount={filteredNotes.length}
        />
      </Box>

      {/* Data Grid */}
      <Box sx={{ flex: 1, px: 3, pb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : filteredNotes.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {unifiedNotes.length === 0
              ? 'No notes found for this patient.'
              : 'No notes match your current filters.'}
          </Alert>
        ) : (
          <DataGrid
            rows={filteredNotes}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            density="comfortable"
            sx={notesGridSx}
            initialState={{
              sorting: {
                sortModel: [{ field: 'timestamp', sort: 'desc' }],
              },
            }}
          />
        )}
      </Box>

      {/* Note Detail Modal */}
      <NoteDetailModal
        open={modalOpen}
        note={selectedNote}
        onClose={() => setModalOpen(false)}
        onViewSource={handleViewSource}
      />
    </Paper>
  );
};
